import { selectIsHost, useActions, useGame } from '@/store/game';
import { RejectionNotice } from '@/ui/RejectionNotice';

/**
 * 07 · Пауза. Состав, правила и единственный выход из партии.
 *
 * Конца у игры нет: компания решает сама, когда хватит, — поэтому «завершить»
 * живёт здесь, рядом с правилами, а не кнопкой на каждом экране. Завершает
 * хост: сервер принимает `finish` только от места 0.
 *
 * Единственный экран с плотным текстом — правила читают трезвым глазом один
 * раз за вечер, а не в темноте по ходу партии.
 */
export function PauseScreen() {
  const state = useGame((s) => s.state);
  const isHost = useGame(selectIsHost);
  const setPaused = useGame((s) => s.setPaused);
  const { finish } = useActions();

  const players = state ? [...state.players].sort((a, b) => a.seat - b.seat) : [];

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)', paddingBottom: 'var(--clown-safe-bottom)' }}
    >
      <div className="flex-1 py-6">
        <div className="flex items-baseline justify-between">
          <h1 className="font-display text-turn">ПАУЗА</h1>
          <button
            type="button"
            onClick={() => setPaused(false)}
            className="min-h-tap px-2 text-meta uppercase tracking-widest text-acc"
          >
            Продолжить
          </button>
        </div>

        <ul className="mt-6 flex flex-col gap-rule border-2 border-line bg-line">
          {players.map((p) => {
            const active = p.seat === state?.activeSeat;
            return (
              <li
                key={p.id}
                className={`flex h-row items-center gap-3 bg-panel px-4 ${active ? 'text-acc' : ''}`}
              >
                <span className="min-w-0 flex-1 truncate text-row">{p.name}</span>
                <span className="text-meta uppercase tracking-widest">
                  спасателей {p.rescuers} · жертв {p.victims}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 text-lead text-dim">
          <p>
            Ходят по кругу. Активный тянет карточку, читает вслух и выполняет — или
            пропускает, если совсем никак.
          </p>
          <p className="mt-3">
            Спасателем задание перекидывают на другого: он у каждого один на партию,
            и на себя его не сыграть.
          </p>
          <p className="mt-3">
            Секретное задание читает только тот, кому оно выпало. Остальные видят,
            что оно есть, но не текст.
          </p>
          <p className="mt-3">
            Конца у игры нет: расходятся, когда надоело. Победителей и очков не бывает.
          </p>
        </div>

        <RejectionNotice />
      </div>

      {isHost && (
        <button
          type="button"
          onClick={finish}
          className="flex h-act w-full items-center justify-center border-2 border-acc bg-ground text-meta uppercase tracking-widest text-acc"
        >
          Завершить партию
        </button>
      )}
    </div>
  );
}
